import type { GetStaticPaths, GetStaticProps } from 'next';
import type { Article, ArticlePreview } from 'interfaces/content/articles/Articles';
import { ArticlePage } from 'pages/Article/V2/ArticlePage';
import NativoTout from 'components/Nativo/NativoTout';
import SubscriptionPlea from 'components/SubscriptionPlea';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getCommonProps, {
  type ArticlePathsParams,
  type CommonProps,
  getArticlePaths,
  isTrinityAdless,
  type Params,
} from 'utils/miscUtils/articlePageHelpers';
import type { PreviewData } from 'interfaces/PreviewData';
import useHeaderCoordinates from 'hooks/useHeaderCoordinates';
import RelatedVideo from 'components/RelatedVideo';
import { Microsites } from 'constants/constants';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan/setarcSpanPageConfig';
import getLatestArticlesBySections from 'api/Section/getLatestArticlesBySections';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import useBreakpoint from '@/main/lib/hooks/useBreakpoint';
import { useOptimizely } from 'providers/OptimizelyProvider';

interface WellArticleProps {
  article: Article;
  latestPosts: ArticlePreview[];
  mostPopular: ArticlePreview[];
  subdomain: string;
  noTrinityAds: boolean;
}

const WellArticle = ({ article, latestPosts, mostPopular, subdomain, noTrinityAds }: WellArticleProps) => {
  const { userFlags: userVariants } = useOptimizely();
  const { bottom } = useHeaderCoordinates();
  const { articleStickyFooter } = userVariants as { articleStickyFooter: { enabled: boolean } };
  const hasStickyFooter = articleStickyFooter?.enabled;
  const isMobile = useBreakpoint('mmMax');
  const hideRecommendedVideo =
    (
      userVariants?.hideRecommendedVideo as {
        variant: string;
      }
    )?.variant === 'on';
  const supressSTNVideo = hasStickyFooter && isMobile;

  const articleEmbeds = [
    {
      component: (
        <NativoTout
          featuredMediaType={article.featuredMediaType}
          key='NativoTout'
        />
      ),
      index: 2,
      node: 'p',
    },
    {
      component: (
        <SubscriptionPlea
          enableSubscriptionPlea={article.postSettings.enableSubscriptionPlea}
          subscriptionPleaTextLined={article.postSettings.subscriptionPleaTextLined}
          pageType={article.pageType}
          key='SubscriptionPlea'
        />
      ),
      index: -1,
      node: '',
    },
  ];

  const isStnVideo = article.relatedVideoType === 'stn_video_media';

  if (!hideRecommendedVideo && (isStnVideo || article.relatedVideoType === 'video_media')) {
    articleEmbeds.push({
      component: (
        <RelatedVideo
          article={article}
          supressStnFloating={isStnVideo && supressSTNVideo}
          key='RelatedVideo'
        />
      ),
      index: 0,
      node: 'p',
    });
  }

  return (
    <ArticlePage
      article={article}
      subdomain={subdomain}
      stickyTop={bottom}
      adFrequency={5}
      lastPWithoutAds={0}
      embeds={articleEmbeds}
      mostPopular={{
        posts: mostPopular,
      }}
      latestPosts={{
        posts: latestPosts,
      }}
      noTrinityAds={noTrinityAds}
    />
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { slug, param, month, day, region } = context.params as Params;

    const commonProps = (await getCommonProps({
      microsite: Microsites.Well,
      preview: context.preview,
      previewData: context.previewData as PreviewData,
      urlParams: { day, month, param, slug },
    })) as CommonProps;

    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(commonProps.articleData),
      fallbackData.arcSpanPageConfig,
    );
    const noAds = !!commonProps.article.adTags.find((tag) => tag.slug === 'no-ads');
    const primarySection = commonProps.articleData.primarySection.name;
    const latestPosts = (
      await safeAsync(
        () => getLatestArticlesBySections([primarySection], 6, 0, Microsites.Well),
        fallbackData.articlesData,
      )
    ).posts;

    const noTrinityAds = isTrinityAdless(commonProps);

    return {
      props: {
        ...commonProps,
        arcSpanPageConfig,
        latestPosts,
        noAds,
        noTrinityAds,
        region,
      },
      revalidate: 60, // In seconds
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<ArticlePathsParams> = async () => {
  if (process.env.TEST) {
    const params = {
      day: '01',
      month: '07',
      param: '2022',
      slug: 'nearly-half-of-workers-say-employers-dont-adequately-support-long-covid-in-the-workplace',
    };
    return {
      fallback: 'blocking',
      paths: [{ params: { ...params, region: 'us' } }, { params: { ...params, region: 'non-us' } }],
    };
  }

  const paths = await safeAsync(() => getArticlePaths(Microsites.Well), fallbackData.paths);

  return {
    fallback: 'blocking',
    paths,
  };
};

export default WellArticle;
